/**
 * Single source of truth for platform WhatsApp config across the app.
 * Hits GET /api/platform/whatsapp once per session, caches in memory +
 * sessionStorage so navigations don't re-fetch.
 *
 * Components must use the values from this hook rather than any local
 * constant — admin can update the number in `/admin/settings` and every
 * surface (float button, landing footer, banners, verification flow)
 * picks it up on next navigation/refresh, no redeploy needed.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const STORAGE_KEY = "platform_wa_v1";
const TTL_MS = 5 * 60 * 1000; // 5-minute cache

const FALLBACK = {
  support_number: "923718067478",
  support_display: "+92 371 8067478",
  support_message: "Hi, mujhe HisaabPro ke baare mein help chahiye",
  welcome_template:
    "Welcome to HisaabPro \u{1F389}\n\nYour account has been verified.\n\nLogin: https://myhisaabpro.com/",
  connected: true,
};

let _cache = null;
let _inflight = null;

function readSession() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (Date.now() - (obj.ts || 0) > TTL_MS) return null;
    return obj.data;
  } catch (err) {
    // sessionStorage access can throw in private-mode or sandboxed iframes.
    console.warn("[platform-wa] readSession failed:", err?.message || err);
    return null;
  }
}

function writeSession(data) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch (err) {
    // Quota or private-mode failures: cache miss is acceptable, just log it.
    console.warn("[platform-wa] writeSession failed:", err?.message || err);
  }
}

async function fetchPlatformWa() {
  if (_cache) return _cache;
  const cached = readSession();
  if (cached) {
    _cache = cached;
    return cached;
  }
  if (_inflight) return _inflight;

  const base = process.env.REACT_APP_BACKEND_URL || "";
  _inflight = axios
    .get(`${base}/api/platform/whatsapp`, { timeout: 6000 })
    .then((r) => {
      const merged = { ...FALLBACK, ...(r.data || {}) };
      _cache = merged;
      writeSession(merged);
      return merged;
    })
    .catch(() => FALLBACK)
    .finally(() => {
      _inflight = null;
    });
  return _inflight;
}

/**
 * Force-refresh the cache. Admin Settings calls this after a successful save
 * so banners/floats see the new number without a page reload.
 */
export function invalidatePlatformWa() {
  _cache = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    console.warn("[platform-wa] invalidate failed:", err?.message || err);
  }
}

/**
 * React hook — returns the latest known platform WA config. Returns the
 * fallback synchronously on first render, then re-renders with the real
 * value once the network call resolves.
 */
export default function usePlatformWa() {
  const [wa, setWa] = useState(_cache || readSession() || FALLBACK);
  useEffect(() => {
    let alive = true;
    fetchPlatformWa().then((d) => {
      if (alive && d) setWa(d);
    });
    return () => { alive = false; };
  }, []);
  return wa;
}

/** Build a wa.me link for an arbitrary number + optional message. */
export function buildWaLink(number, message) {
  const digits = String(number || "").replace(/\D/g, "");
  if (!digits) return "#";
  if (message) return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
  return `https://wa.me/${digits}`;
}
